import React, { useState } from "react";
import {
  Button,
  Skeleton,
  Card,
  CardBody,
  cn,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  Image,
} from "@heroui/react";
import { useGetProducts, type Product } from "../../hooks/useGetProducts";
import { useWalletVerification } from "../../hooks/useWalletVerification";
import { usePurchaseProduct } from "../../hooks/usePurchaseProduct";
import { useGetAccountInfo } from "@multiversx/sdk-dapp/hooks";
import { logout } from "@multiversx/sdk-dapp/utils";
import { IMAGES } from "../../localConstants/images";
import useModalStore from "../../store/useModalStore";

interface ProductCardProps {
  product: Product;
  onPurchase: (product: Product) => void;
}

const ProductCard = ({ product, onPurchase }: ProductCardProps) => {
  // Determine tier based on image URL to set progressive sizes
  const getTierFromImage = (imageUrl: string): number => {
    if (imageUrl.includes("tier-1") || imageUrl.includes("-1.png")) return 1;
    if (imageUrl.includes("2") || imageUrl.includes("-2.png")) return 2;
    if (imageUrl.includes("tier-3") || imageUrl.includes("-3.png")) return 3;
    if (imageUrl.includes("tier-4") || imageUrl.includes("-4.png")) return 4;
    return 1; // default
  };

  const tier = getTierFromImage(product.image);

  // Progressive sizes based on tier
  const getImageSize = (tier: number) => {
    switch (tier) {
      case 1:
        return "w-12 h-12 md:w-14 md:h-14";
      case 2:
        return "w-14 h-14 md:w-[60px] md:h-[60px]";
      case 3:
        return "w-full h-full max-w-[84px] max-h-[84px]";
      case 4:
        return "w-full h-full max-w-[96px] max-h-[88px]";
      default:
        return "w-12 h-12 md:w-14 md:h-14";
    }
  };

  return (
    <Card
      className={`flex-shrink-0 w-44 md:w-auto md:flex-shrink h-52 md:h-64 ${
        product.isPopular ? "" : "bg-[#0D0D0D]"
      }`}
      style={{
        backgroundColor: product.isPopular ? "#3CB770" : undefined,
      }}
    >
      <CardBody className="flex flex-col items-center justify-between p-3 md:p-4 text-center h-full overflow-hidden">
        <p
          className={cn(
            product.isPopular
              ? "text-white text-sm md:text-lg font-bold"
              : "text-green-400 text-sm md:text-base font-semibold"
          )}
        >
          {product.price.toLocaleString()} $A1X
        </p>
        <div className="relative">
          <img
            src={product.image}
            alt={product.name}
            className={cn(getImageSize(tier), "object-contain")}
          />
        </div>
        <div className="text-center">
          <p className={cn("text-white text-xl md:text-3xl font-bold")}>
            {product.rewardAmount.toLocaleString("en-US", {
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            })}
          </p>
        </div>
        <Button
          className={cn(
            "w-full font-medium border-0 text-xs",
            product.isPopular
              ? "bg-[white] text-black"
              : "bg-green-500 hover:bg-green-600 text-white"
          )}
          size="sm"
          onPress={() => onPurchase(product)}
        >
          BUY
        </Button>
      </CardBody>
    </Card>
  );
};

interface ProductSectionProps {
  title: string;
  products: Product[];
  icon: string;
  onPurchase: (product: Product) => void;
}

const ProductSection = ({
  title,
  products,
  icon,
  onPurchase,
}: ProductSectionProps) => {
  return (
    <div className="mb-6 md:mb-8">
      <div className="flex items-center gap-2 mb-3 md:mb-4">
        <img src={icon} alt={title} className="w-5 h-5 md:w-6 md:h-6" />
        <h2 className="text-white text-lg md:text-xl font-semibold">{title}</h2>
        {title === "Common Currency" && (
          <img
            src="/70-off.png"
            alt="70% off"
            className="h-5 md:h-6 object-contain"
          />
        )}
        {title === "Credits" && (
          <img
            src="/40-off.png"
            alt="40% off"
            className="h-5 md:h-6 object-contain"
          />
        )}
      </div>

      {/* Mobile: Horizontal scroll */}
      <div className="md:hidden">
        <div className="flex gap-3 overflow-x-auto pb-2">
          {products.map((product) => (
            <ProductCard
              key={product.id}
              product={product}
              onPurchase={onPurchase}
            />
          ))}
        </div>
      </div>

      {/* Desktop: Grid */}
      <div className="hidden md:grid md:grid-cols-4 gap-4">
        {products.map((product) => (
          <ProductCard
            key={product.id}
            product={product}
            onPurchase={onPurchase}
          />
        ))}
      </div>
    </div>
  );
};

export const Marketplace = () => {
  const { address } = useGetAccountInfo();
  const { data: products, isLoading, error } = useGetProducts();
  const { data: walletVerification, isLoading: isVerificationLoading } =
    useWalletVerification();
  const purchaseProduct = usePurchaseProduct();
  const { setConnectWalletModalOpen } = useModalStore();
  const [showWalletModal, setShowWalletModal] = useState(false);

  const handlePurchase = (product: Product) => {
    if (!address) {
      setConnectWalletModalOpen(true);
      return;
    }

    if (!walletVerification?.valid) {
      setShowWalletModal(true);
      return;
    }

    purchaseProduct.mutate({
      productId: product.id,
      productName: product.name,
    });
  };

  // Show wallet linking modal when manually triggered
  const shouldShowWalletModal =
    showWalletModal &&
    !!address &&
    !isVerificationLoading &&
    walletVerification &&
    !walletVerification.valid;

  if (isLoading) {
    return (
      <div className="space-y-8">
        {/* Banner Skeleton */}
        <Skeleton className="w-full h-48 rounded-lg" />

        {/* Products Skeleton */}
        <div className="space-y-6">
          <Skeleton className="h-8 w-48" />
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-64 rounded-lg" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-400">
          Error loading products. Please try again.
        </p>
      </div>
    );
  }

  const currencyProducts = products?.filter((p) => p.type === "currency") || [];
  const aiCreditsProducts =
    products?.filter((p) => p.type === "ai-credits") || [];

  return (
    <div className="space-y-6 md:space-y-8">
      {/* Currency Shop Banner */}
      <div className="relative overflow-hidden rounded-lg h-32 md:h-48">
        <img
          src={IMAGES.CURRENCY_BANNER}
          alt="Currency Shop Banner"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-transparent" />
        <div className="absolute inset-0 flex items-center justify-center"></div>
      </div>

      {/* Common Currency Section */}
      <ProductSection
        title="Common Currency"
        products={currencyProducts}
        icon={IMAGES.GOLD}
        onPurchase={handlePurchase}
      />

      {/* AI Credits Section */}
      <ProductSection
        title="Credits"
        products={aiCreditsProducts}
        icon={IMAGES.GEMS}
        onPurchase={handlePurchase}
      />

      {/* Wallet Linking Required Modal */}
      <Modal
        isOpen={shouldShowWalletModal}
        onOpenChange={(open: boolean) => !open && setShowWalletModal(false)}
        isDismissable={true}
        hideCloseButton={false}
        classNames={{
          base: "bg-neutral-900 border border-neutral-700",
          backdrop: "bg-black/50 backdrop-blur-sm",
        }}
      >
        <ModalContent>
          <ModalHeader className="flex flex-col gap-1 text-center">
            <img
              src={IMAGES.AINEXUS}
              alt="AiNexus"
              className="h-12 w-auto mx-auto mb-2"
            />
            <h2 className="text-white text-xl font-bold">
              Wallet Linking Required
            </h2>
          </ModalHeader>
          <ModalBody className="pb-6">
            <div className="text-center space-y-4">
              <p className="text-gray-300 text-sm">
                Your wallet is connected but not linked to your AiNexus account.
              </p>
              <p className="text-gray-400 text-sm">
                Please download and use the AiNexus mobile app to link your
                wallet before making purchases.
              </p>

              <div className="bg-neutral-800 rounded-lg p-4 my-4">
                <h3 className="text-white font-semibold text-sm mb-2">
                  How to link your wallet:
                </h3>
                <ol className="text-gray-400 text-xs space-y-1 text-left">
                  <li>1. Download the AiNexus mobile app</li>
                  <li>2. Create or log into your account</li>
                  <li>3. Navigate to wallet settings</li>
                  <li>4. Link your MultiversX wallet</li>
                  <li>5. Return here to make purchases</li>
                </ol>
              </div>

              <div className="flex flex-col gap-4">
                <div className="flex justify-center space-x-3">
                  <a
                    href="https://play.google.com/store/apps/details?id=com.bullseye.ainexus&pli=1"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Image
                      src={IMAGES.GOOGLE_PLAY}
                      alt="Google Play"
                      className="h-12 w-auto object-contain rounded-none"
                    />
                  </a>
                  <a
                    href="https://apps.apple.com/us/app/ai-nexus-app/id6745814870"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Image
                      src={IMAGES.APP_STORE}
                      alt="App Store"
                      className="h-12 w-auto object-contain rounded-none"
                    />
                  </a>
                </div>

                <div>
                  <Button
                    variant="bordered"
                    className="w-full border-neutral-600 text-white"
                    onPress={() => {
                      // Refresh verification status
                      window.location.reload();
                    }}
                  >
                    I've Linked My Wallet
                  </Button>

                  <div className="text-center">
                    <a
                      onClick={() => {
                        logout();
                      }}
                      className="text-neutral-400 text-sm underline cursor-pointer"
                    >
                      Change wallet
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </ModalBody>
        </ModalContent>
      </Modal>
    </div>
  );
};
